/* GWP (gift-with-purchase) manager.
   Reads #gwp-config (snippets/gwp-config.liquid), evaluates via GWPRules
   (assets/gwp-rules.js), and keeps the gift line in sync with cart state.
   The gift is an enhancement: every failure path degrades to a normal cart. */
(function () {
  'use strict';

  var OPT_IN_KEY = 'gwp_opt_in';
  var DEBUG_KEY = 'gwp_debug';

  // Debug logging is on when the offer metaobject's `debug` field is true, OR when
  // localStorage gwp_debug === 'true' (the localStorage override works even when the
  // config itself is missing/broken — set it in the console, then reload):
  //   localStorage.setItem('gwp_debug', 'true')
  var debugOn = (function () {
    try { return localStorage.getItem(DEBUG_KEY) === 'true'; } catch (e) { return false; }
  })();

  function dbg() {
    if (!debugOn) return;
    var args = ['[gwp]'];
    for (var i = 0; i < arguments.length; i++) args.push(arguments[i]);
    console.info.apply(console, args);
  }

  function readConfig() {
    var el = document.getElementById('gwp-config');
    if (!el) {
      dbg('boot: #gwp-config element NOT found — is {%- render \'gwp-config\' -%} in theme.liquid and the snippet uploaded?');
      return null;
    }
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      dbg('boot: #gwp-config exists but its JSON failed to parse:', e.message, el.textContent);
      return null;
    }
  }

  function optedIn() {
    try { return localStorage.getItem(OPT_IN_KEY) === 'true'; } catch (e) { return false; }
  }

  function setOptedIn(value) {
    try {
      if (value) localStorage.setItem(OPT_IN_KEY, 'true');
      else localStorage.removeItem(OPT_IN_KEY);
    } catch (e) { /* private mode — cart attribute still works for this session */ }
  }

  function GWP(config) {
    this.config = config;
    this.syncing = false;
    this.pending = false;
    this.addFailed = false; // set on OOS/non-free add; retried on next page load only
    // Set by the Klaviyo opt-in handler, consumed by the next sync: the one case where
    // a successful, $0-verified gift add OPENS the drawer (customer-initiated — they just
    // completed the opt-in form, so showing the gift land is confirmation, not a hijack).
    this.wantsDrawerOpen = false;
    this.klaviyoFormOpen = false;   // is the configured Klaviyo form currently on screen?
    this.pendingDrawerOpen = false; // gift verified while the form was still open — open on close
    this.sawFormSubmit = false;     // profile step submitted (multi-step completion tracking)
    this.miniCart = document.querySelector('mini-cart');
    // The cart page's own <cart-items> custom element (sections/main-cart-items.liquid).
    // The drawer's lazy-loaded content also contains a <cart-items>, but it isn't in the
    // DOM at boot time (header.liquid renders only a spinner until first open), so gate on
    // the #main-cart-items section wrapper — the same signal cart.js keys off — rather
    // than relying on that timing. Non-cart pages fall back to the mini-cart-only path.
    var mainCartSection = document.getElementById('main-cart-items');
    this.cartItems = mainCartSection && mainCartSection.dataset.id ? document.querySelector('cart-items') : null;
  }

  // Mirrors CartItems.getSectionsToRender()/updateQuantity() (assets/cart.js): on the cart
  // page, request the same section ids cart.js itself requests (main-cart-items,
  // main-cart-footer, cart-icon-bubble, etc. — whatever that element's own list contains),
  // keyed by `.section` (the actual Shopify section id), not `.id` (the DOM element id).
  // Falls back to the mini-cart-only set when there's no cart-page element on this page.
  GWP.prototype.sectionIds = function () {
    if (this.cartItems) {
      return this.cartItems.getSectionsToRender()
        .map(function (s) { return s.section; })
        .filter(Boolean);
    }
    if (this.miniCart) return this.miniCart.getSectionsToRender().map(function (s) { return s.id; });
    return [];
  };

  GWP.prototype.fetchCart = function () {
    return fetch(theme.routes.cart_url + '.js').then(function (res) { return res.json(); });
  };

  GWP.prototype.mutate = function (url, body) {
    var self = this;
    body.sections = this.sectionIds();
    body.sections_url = window.location.pathname;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (res.ok) {
          // Background mutation — update cart sections in place, but never call
          // miniCart.open()/renderContents() here: that would pop the drawer open
          // for syncs the customer didn't initiate (page-load auto-add, cleanup,
          // stale-line removal). Same silence applies to the cart-page path: no focus
          // changes, no scrolling — just an in-place HTML swap.
          if ((self.cartItems || self.miniCart) && data.sections) self.renderSections(data);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp' });
        }
        return { ok: res.ok, status: res.status, data: data };
      });
    });
  };

  // On the cart page, mirrors CartItems.updateQuantity()'s section-update loop
  // (assets/cart.js): for each { id, section, selector } entry, find the element by `id`,
  // find its `selector` child within it (falling back to the element itself), and replace
  // that child's innerHTML with the matching `.section`-keyed HTML from the response —
  // same targets cart.js itself updates (.js-contents on #main-cart-items /
  // #main-cart-footer, .shopify-section on the icon bubbles and #mini-cart, etc.).
  // Off the cart page, mirrors MiniCart.renderContents' section-update loop
  // (assets/cart-drawer.js) instead. Neither path opens the drawer or moves focus —
  // see the comment in mutate() above.
  GWP.prototype.renderSections = function (data) {
    if (this.cartItems) {
      this.cartItems.getSectionsToRender().forEach(function (section) {
        var el = document.getElementById(section.id);
        if (!el || !data.sections[section.section]) return;
        var target = el.querySelector(section.selector) || el;
        target.innerHTML = this.cartItems.getSectionInnerHTML(data.sections[section.section], section.selector);
      }, this);
      return;
    }
    this.miniCart.getSectionsToRender().forEach(function (section) {
      var el = document.getElementById(section.id);
      if (el) el.innerHTML = this.miniCart.getSectionInnerHTML(data.sections[section.id], section.selector);
    }, this);
  };

  GWP.prototype.setOptInAttribute = function (value) {
    var attributes = {};
    attributes[OPT_IN_KEY] = value;
    return this.mutate(theme.routes.cart_update_url + '.js', { attributes: attributes });
  };

  GWP.prototype.addGift = function (openDrawerOnSuccess) {
    var self = this;
    dbg('addGift: adding variant', this.config.giftVariantId, 'with _gwp =', this.config.offerHandle);
    return this.mutate(theme.routes.cart_add_url + '.js', {
      items: [{ id: this.config.giftVariantId, quantity: 1, properties: { _gwp: this.config.offerHandle } }],
    }).then(function (add) {
      if (!add.ok) {
        self.addFailed = true; // e.g. 422 gift out of stock
        dbg('addGift: FAILED (HTTP ' + add.status + ') — out of stock or invalid variant? Backing off until next page load.', add.data);
        return;
      }
      // Identify the line we just added by its key so verification below can never
      // land on a different line of the same variant (e.g. one the customer paid for).
      var addedItem = add.data && add.data.items && add.data.items[0];
      var addedKey = addedItem && addedItem.key;
      // $0 verification: never show a "free" gift the customer would be charged for.
      // The fresh fetch is deliberate — the add response may not yet reflect
      // automatic-discount pricing; the fresh cart is authoritative.
      return self.fetchCart().then(function (fresh) {
        var giftLine = null;
        if (addedKey) {
          giftLine = fresh.items.find(function (i) { return i.key === addedKey; });
        }
        if (!giftLine) {
          giftLine = fresh.items.find(function (i) {
            return i.properties && i.properties._gwp === self.config.offerHandle;
          });
        }
        if (giftLine && giftLine.final_line_price !== 0) {
          self.addFailed = true;
          dbg('$0 verification: FAILED — added gift line priced at ' + giftLine.final_line_price + '¢, expected 0. Removing it. Check the SupaEasy discount is active and ALL its qualifiers pass for this cart.');
          return self.mutate(theme.routes.cart_change_url + '.js', { id: giftLine.key, quantity: 0 });
        }
        dbg('$0 verification: OK — gift line is free' + (giftLine ? ' (key=' + giftLine.key + ')' : ' (line not found in fresh cart — nothing to verify)'));
        if (openDrawerOnSuccess && giftLine) self.openDrawer();
      });
    });
  };

  // The one deliberate exception to the silent-render rule: when the customer just
  // completed the opt-in form and the gift verified at $0, opening the drawer is
  // confirmation of an action they took — not a background hijack. MiniCart.open()
  // also lazy-loads the drawer's content if this is its first open.
  GWP.prototype.openDrawer = function () {
    if (!this.miniCart || typeof this.miniCart.open !== 'function') return;
    // Never open over a Klaviyo form the customer is still interacting with (email-only
    // mode adds the gift while the SMS step is up; success steps also linger) — hold the
    // open until the form's `close` event fires.
    if (this.klaviyoFormOpen) {
      this.pendingDrawerOpen = true;
      dbg('drawer open deferred — the Klaviyo form is still on screen (opens on its close event)');
      return;
    }
    try {
      dbg('opening cart drawer — gift added and verified after opt-in');
      this.miniCart.open();
    } catch (e) { /* best-effort: a missing drawer element must never break the flow */ }
  };

  GWP.prototype.cleanup = function (cart) {
    var self = this;
    var gifts = cart.items.filter(function (i) { return i.properties && i.properties._gwp; });
    dbg('cleanup: offer inactive — removing ' + gifts.length + ' gift line(s), clearing opt-in attribute/localStorage');
    var chain = Promise.resolve();
    gifts.forEach(function (giftLine) {
      chain = chain.then(function () {
        return self.mutate(theme.routes.cart_change_url + '.js', { id: giftLine.key, quantity: 0 });
      });
    });
    return chain.then(function () {
      if (cart.attributes && cart.attributes[OPT_IN_KEY]) return self.setOptInAttribute('');
    }).then(function () {
      setOptedIn(false);
    });
  };

  GWP.prototype.sync = function (reason) {
    var self = this;
    if (this.syncing) { this.pending = true; dbg('sync(' + reason + '): already syncing — queued'); return Promise.resolve(); }
    this.syncing = true;
    return this.fetchCart()
      .then(function (cart) {
        dbg('sync(' + reason + '): cart has ' + cart.items.length + ' item(s), items_subtotal ' + cart.items_subtotal_price + '¢, optedIn(localStorage)=' + optedIn() + ', attribute=' + JSON.stringify(cart.attributes && cart.attributes[OPT_IN_KEY]));
        var result = GWPRules.evaluateOffer(self.config, cart, { optedIn: optedIn() });
        if (debugOn && result.trace) {
          for (var t = 0; t < result.trace.length; t++) dbg('  · ' + result.trace[t]);
        }
        // Consume the drawer-open request set by the Klaviyo submit handler. It applies
        // to THIS sync only (or its queued follow-up) — a submit that doesn't result in
        // a verified add (under threshold, OOS) must not leave a stale flag behind.
        var openDrawerOnAdd = self.wantsDrawerOpen;
        self.wantsDrawerOpen = false;
        if (result.action === 'cleanup') return self.cleanup(cart);

        var step = Promise.resolve();
        // Stale gift lines (offer's gift variant was edited): remove before anything else —
        // they are full-price leftovers the customer never chose.
        (result.staleLines || []).forEach(function (staleLine) {
          step = step.then(function () {
            dbg('removing stale gift line', staleLine.key);
            return self.mutate(theme.routes.cart_change_url + '.js', { id: staleLine.key, quantity: 0 });
          });
        });
        if (optedIn() && (!cart.attributes || cart.attributes[OPT_IN_KEY] !== 'true')) {
          step = step.then(function () {
            dbg('setting cart attribute ' + OPT_IN_KEY + '=true (from localStorage — new cart or attribute lost)');
            return self.setOptInAttribute('true'); // opt-in survives a new cart via localStorage
          });
        }
        return step.then(function () {
          if (result.action === 'add') {
            if (self.addFailed) { dbg('add skipped: a previous add failed this page load (backoff — reload the page to retry)'); return; }
            return self.addGift(openDrawerOnAdd);
          }
          if (result.action === 'remove') {
            dbg('removing gift line', result.giftLine.key);
            return self.mutate(theme.routes.cart_change_url + '.js', { id: result.giftLine.key, quantity: 0 });
          }
          if (result.action === 'clamp') {
            dbg('clamping gift line', result.giftLine.key, 'to quantity 1');
            return self.mutate(theme.routes.cart_change_url + '.js', { id: result.giftLine.key, quantity: 1 });
          }
        });
      })
      .catch(function (e) { console.error('[gwp]', e); })
      .then(function () {
        self.syncing = false;
        if (self.pending) { self.pending = false; return self.sync('queued'); }
      });
  };

  GWP.prototype.bind = function () {
    var self = this;
    var c = this.config;
    // Fully unconfigured/inert store: no active offer, no leftover gift line, no leftover
    // opt-in attribute. Nothing on this page's lifetime could ever need cleanup, so don't
    // bind anything — there'd be nothing to unbind either. Active stores (and stores with
    // something left over to clean up after a disable) keep exactly today's behavior below.
    if (!c.active && !c.cartHasGift && !c.hasOptInAttribute) {
      dbg('bind: skipped — offer inactive and nothing to clean (fully inert store)');
      return;
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
      if (evt && evt.source === 'gwp') return; // never react to our own mutations
      self.sync('cart-update');
    });
    document.addEventListener('cartdrawer:opened', function () { self.sync('drawer-opened'); });
    dbg('bind: cart listeners registered (cartUpdate, cartdrawer:opened)');

    if (c.active && c.requireOptIn) {
      window.addEventListener('klaviyoForms', function (e) {
        if (!e.detail || !e.detail.type) return;
        var d = e.detail;
        var relevant = ['open', 'embedOpen', 'close', 'stepSubmit', 'submit'].indexOf(d.type) !== -1;
        if (!relevant) return;
        if (self.config.klaviyoFormId && d.formId !== self.config.klaviyoFormId) {
          if (d.type === 'submit') dbg('klaviyoForms: submit from form "' + d.formId + '" ignored — configured klaviyo_form_id is "' + self.config.klaviyoFormId + '"');
          return;
        }
        if (debugOn) dbg('klaviyoForms: ' + d.type + ' detail=' + JSON.stringify(d));

        // Track whether the form is on screen — a verified gift add never opens the
        // drawer over a form the customer is still filling in (see openDrawer()).
        if (d.type === 'open' || d.type === 'embedOpen') { self.klaviyoFormOpen = true; return; }
        if (d.type === 'close') {
          self.klaviyoFormOpen = false;
          if (self.pendingDrawerOpen) { self.pendingDrawerOpen = false; self.openDrawer(); }
          return;
        }

        // Klaviyo semantics (observed + logged): on a multi-step form, `submit` fires at
        // the PROFILE step (email) together with that step's `stepSubmit`; later data
        // steps (e.g. SMS) fire only `stepSubmit`. So:
        //   optInRequiresCompletion=false → opt-in at `submit` (email captured).
        //   optInRequiresCompletion=true  → opt-in at the first `stepSubmit` AFTER
        //     `submit` (the post-profile data step — SMS on a 2-step form). Dismissing
        //     the form at that step fires `close`, not `stepSubmit`, so abandoners
        //     correctly get nothing in this mode.
        var completes = false;
        if (self.config.optInRequiresCompletion) {
          if (d.type === 'submit') {
            self.sawFormSubmit = true;
            dbg('klaviyoForms: profile submitted — waiting for the final step (opt_in_requires_completion is on)');
            return;
          }
          completes = d.type === 'stepSubmit' && self.sawFormSubmit;
          if (!completes) return;
        } else {
          if (d.type !== 'submit') return;
          completes = true;
        }

        dbg('klaviyoForms: opt-in captured (' + (self.config.optInRequiresCompletion ? 'full form completion' : 'profile submit') + ') — syncing');
        setOptedIn(true);
        self.wantsDrawerOpen = true;
        self.sync('klaviyo-submit');
      });
      dbg('bind: klaviyoForms listener registered (form filter: ' + (c.klaviyoFormId || 'any Klaviyo form') + ')');
      document.addEventListener('click', function (e) {
        var el = e.target.closest ? e.target.closest('[data-gwp-claim]') : null;
        if (!el || !self.config.klaviyoFormId) return;
        e.preventDefault();
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['openForm', self.config.klaviyoFormId]);
      });
    }
  };

  GWP.prototype.start = function () {
    this.bind();
    var c = this.config;
    if (!c.active) {
      // Offer turned off/expired: only touch the network if there is something to clean.
      if (c.cartHasGift || c.hasOptInAttribute) {
        dbg('start: offer inactive but cart has leftovers — running cleanup sync');
        this.sync('initial-cleanup');
      } else {
        dbg('start: offer inactive (config active:false — no active metaobject entry visible to Liquid, or outside its date window). Idle.');
      }
      return;
    }
    // Nothing can change until the customer opts in — skip the startup fetch.
    if (c.requireOptIn && !optedIn() && !c.cartHasGift) {
      dbg('start: offer active; waiting for opt-in (no localStorage flag, no gift in cart). Submit the Klaviyo form — or for testing: localStorage.setItem(\'gwp_opt_in\',\'true\') then change the cart.');
      return;
    }
    dbg('start: offer active — running initial sync');
    this.sync('initial');
  };

  function globalsReady() {
    return typeof subscribe !== 'undefined' && typeof GWPRules !== 'undefined' &&
      typeof theme !== 'undefined' && !!theme.routes;
  }

  function boot() {
    var config = readConfig();
    if (config && config.debug) debugOn = true; // metaobject debug field turns logging on
    dbg('boot: gwp.js loaded (debug via ' + (config && config.debug ? 'metaobject debug field' : 'localStorage gwp_debug') + ')');
    if (!config) return; // readConfig already logged the reason when debug is forced on
    dbg('boot: config =', config);
    if (!globalsReady()) {
      // Some brand themes load pubsub.js with `async`, so it may not have executed when
      // this deferred script runs. Retry once when the page finishes loading; if the
      // globals still aren't there, stay inert (failure posture: normal cart).
      dbg('boot: theme globals not ready yet (subscribe=' + (typeof subscribe !== 'undefined') + ', GWPRules=' + (typeof GWPRules !== 'undefined') + ', theme.routes=' + (typeof theme !== 'undefined' && !!theme.routes) + ') — retrying on window load');
      if (document.readyState !== 'complete') {
        window.addEventListener('load', function () {
          if (globalsReady()) {
            dbg('boot: globals ready after window load — starting');
            new GWP(config).start();
          } else {
            dbg('boot: globals STILL missing after window load — staying inert. pubsub.js/global.js failed to load?');
          }
        }, { once: true });
      } else {
        dbg('boot: page already fully loaded and globals missing — staying inert');
      }
      return;
    }
    new GWP(config).start();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
